<template>
    <div>
      <CRUDCriteria :showReset="true" :showSearch="false">
        <template v-slot:criteriaRows>
            <v-row class="mr-6 ml-6 mb-n6" dense>
                <v-col cols='2'>
                    <ClarionDateControl
                      class="input-text-size"
                      v-model="criteriaState.fromDate"
                      label="From Date"
                      :isToggle="false"
                      @blurred="searchByCriteria(), changeTheCriteriaFromDate()"
                    />
                    </v-col>
                    <v-col cols='2'>
                    <ClarionDateControl
                      class="input-text-size"
                        v-model="criteriaState.toDate"
                      label="To Date"
                      :isToggle="false"
                      @blurred="searchByCriteria(), changeTheCriteriaToDate()"
                    />
                    </v-col>
                    <v-col cols="2" class="mt-4">
                  <BinaryToggle
                id="apportionmentRecordTypeToggle"
                aria-label="Record type: Normal or Trial"
                off-label="Normal"
                on-label="Trial"
                :value="criteriaState.typeOfRecord"
                @input="onChangeRecordType" />
                </v-col>
                <v-col cols="4" class="input-text-size align-right mt-5">
                    <v-btn color="primary" small dense
                    @click="lockRecords">
                      Lock Records</v-btn>
                    <v-btn class="ml-3" color="primary" small dense @click="unLockRecords">
                      Unlock Records</v-btn>
                    <v-btn
                      :disabled="!isSuperAdmin"
                      class="ml-3"
                      color="primary"
                      small
                      dense
                      @click="deleteRecords"
                    >
                      DELETE
                    </v-btn>
                </v-col>
                </v-row>
                <v-row dense class="mt-0 ml-4">
                  <p class="mt-4">Records with negative amount:
                    <span class="red--text">{{ totalNegativeCount }}</span>
                    <span v-if="totalNegativeCount > 0" class="ml-3">
                      <span class="negative-warning input-text-size">Further action may be required</span>
                      <a class="more-info-link ml-1 input-text-size" @click.prevent="showNegativeInfo = !showNegativeInfo">
                        {{ showNegativeInfo ? 'less info' : 'more info' }}
                      </a>
                    </span>
                  </p>
                  <p v-if="totalNegativeCount > 0 && showNegativeInfo" class="negative-info-text ml-2 mt-n2">
                    Negative apportionment records are usually not a problem.
                    But if the negative apportionment amount needs to be fixed,
                    find and open the red records and click the "Move to Resale" button.
                  </p>
                </v-row>
            </template>
      </CRUDCriteria>
    </div>
  </template>

<script>
import { mapMutations, mapGetters, mapActions } from 'vuex';
import BinaryToggle from '@/components/common/base/BinaryToggle.vue';
import ClarionDateControl from '@/components/common/ClarionDateControl.vue';
import CRUDCriteria from '../../common/base/crud-2.0/CRUDCriteria.vue';
import '../../../assets/style.css';
import { makeCriteriaStateNullIfEmptyString } from '../../tax/common-utils/CommonTaxFunctionalities';
import { toIsoDate } from '../../../util/shared/tmc-global';
import { isAuthorised } from '../../../store/auth/token';
import { authRoles } from '../../../router/roleGroups';

export default {
  data() {
    return {
      fromDateValue: '',
      toDateValue: '',
      isRecordTypeSwitch: false,
      showNegativeInfo: false,
    };
  },
  props: {
    baseCrudKey: {
      type: String,
      default: '',
    },
    totalNegativeCount: {
      type: Number,
      default: null,
    },
  },
  components: {
    BinaryToggle,
    ClarionDateControl,
    CRUDCriteria,
  },
  created() {
    this.getCriteria();
  },
  computed: {
    ...mapGetters('base/crud', ['criteria']),
    criteriaStateForTaxesApport: {
      get() {
        return this.criteria('taxesApprotionment');
      },
      set(value) {
        this.setCriteria(['taxesApprotionment', value]);
      },
    },
    criteriaStateForMiscMtg: {
      get() {
        return this.criteria('miscMtgBaseCrudKey');
      },
      set(value) {
        this.setCriteria(['miscMtgBaseCrudKey', value]);
      },
    },
    criteriaState: {
      get() {
        return this.criteria(this.baseCrudKey);
      },
      set(value) {
        this.setCriteria([this.baseCrudKey, value]);
      },
    },
    isSuperAdmin() {
      return isAuthorised(authRoles.SuperAdmin) || isAuthorised(authRoles.UserAdmins);
    },
  },
  methods: {
    ...mapMutations('base/crud', [
      'setCriteria',
    ]),
    ...mapActions(['reAuth', 'flashWarn']),
    ...mapActions(['reAuth', 'flashError']),
    ...mapActions('apportionment/apportionmentTab', [
      'upsertForLockRecords', 'deleteRecordFromApportionment',
    ]),
    ...mapActions('tax/tifDistricts', [
      'updateTifAfterApportionmentLocked',
    ]),
    searchByCriteria() {
      const returnCriteria = makeCriteriaStateNullIfEmptyString(this.criteriaState);
      this.criteriaState = { ...returnCriteria };
      this.$emit('loadCRUDList');
    },
    changeTheCriteriaFromDate() {
      this.criteriaStateForTaxesApport.fromDateValue = this.criteriaState.fromDate;
      this.criteriaStateForMiscMtg.fromDate = this.criteriaState.fromDate;
    },
    changeTheCriteriaToDate() {
      this.criteriaStateForTaxesApport.toDateValue = this.criteriaState.toDate;
      this.criteriaStateForMiscMtg.toDate = this.criteriaState.toDate;
    },
    // What v-model + @change did on the switch: write the flag the criteria carries, then let
    // changeRecordTypeSwitch reload against it.
    onChangeRecordType(val) {
      this.criteriaState.typeOfRecord = val;
      this.changeRecordTypeSwitch();
    },
    changeRecordTypeSwitch() {
      // this.isRecordTypeSwitch = !this.isRecordTypeSwitch;
      this.$emit('loadCRUDList');
    },
    // upsertForLockRecords resolves {} when the request itself failed (and has already flashed
    // an error), and the backend reports a failed lock/unlock as result.ok === 0.
    lockUnlockSucceeded(res, operation) {
      if (!res || !Object.keys(res).length) return false;
      if (res.result && res.result.ok === 0) {
        this.flashError(`Records could not be ${operation}ed.`);
        return false;
      }
      return true;
    },
    // The TIF District File is recalculated through the last locked month, so it has to run
    // after the lock/unlock has finished, and only if it worked.
    async lockRecords() {
      const { baseCrudKey } = this;
      const convertToIsoFromDate = this.criteriaState.fromDate
        ? toIsoDate(this.criteriaState.fromDate) : null;
      const convertToIsoToDate = this.criteriaState.toDate
        ? toIsoDate(this.criteriaState.toDate) : null;
      const criteria = {
        // ...this.criteriaState,
        ...{
          fromDate: convertToIsoFromDate,
          toDate: convertToIsoToDate,
          operation: 'lock',
        },
      };
      const res = await this.upsertForLockRecords({ baseCrudKey, criteria });
      this.$emit('loadCRUDList');
      if (this.lockUnlockSucceeded(res, 'lock')) {
        await this.updateTifCollectionRecords();
      }
    },
    updateTifCollectionRecords() {
      const { baseCrudKey } = this;
      const convertToIsoFromDate = this.criteriaState.fromDate
        ? toIsoDate(this.criteriaState.fromDate) : null;
      const convertToIsoToDate = this.criteriaState.toDate
        ? toIsoDate(this.criteriaState.toDate) : null;
      const criteria = {
        // ...this.criteriaState,
        ...{
          fromDate: convertToIsoFromDate,
          toDate: convertToIsoToDate,
        },
      };
      return this.updateTifAfterApportionmentLocked({ baseCrudKey, criteria });
    },
    async unLockRecords() {
      const { baseCrudKey } = this;
      const convertToIsoFromDate = this.criteriaState.fromDate
        ? toIsoDate(this.criteriaState.fromDate) : null;
      const convertToIsoToDate = this.criteriaState.toDate
        ? toIsoDate(this.criteriaState.toDate) : null;
      const criteria = {
        // ...this.criteriaState,
        ...{
          fromDate: convertToIsoFromDate,
          toDate: convertToIsoToDate,
          operation: 'unlock',
        },
      };
      const res = await this.upsertForLockRecords({ baseCrudKey, criteria });
      this.$emit('loadCRUDList');
      if (this.lockUnlockSucceeded(res, 'unlock')) {
        await this.updateTifCollectionRecords();
      }
    },
    deleteRecords() {
      const { baseCrudKey } = this;
      const collectionName = 'apportionment';
      if (this.isFiltersAvailable()) {
        if (window.confirm('Are you sure you want to delete these items?')) {
          const initialCriteria = {
            fromDate: this.criteriaState.fromDate
              ? toIsoDate(this.criteriaState.fromDate) : null,
            toDate: this.criteriaState.toDate
              ? toIsoDate(this.criteriaState.toDate) : null,
            typeOfRecord: this.criteriaState.typeOfRecord || false,
            collectionName,
          };
          this.deleteRecordFromApportionment({ baseCrudKey, criteria: initialCriteria });
          this.$emit('loadCRUDList');
        }
      } else {
        this.flashWarn('Please select date range!');
      }
    },
    isFiltersAvailable() {
      return (this.criteriaState.fromDate && this.criteriaState.toDate);
    },
    getCriteria() {
      const res = this.criteria('taxesApprotionment');
      if (res) {
        this.criteriaState.fromDate = res.fromDateValue;
        this.criteriaState.toDate = res.toDateValue;
      }
    },
  },
};
</script>

<style scoped>
.align-right {
  display: flex;
  justify-content: flex-end;
}
.negative-warning {
  color: red;
  background-color: yellow;
  font-weight: bold;
  padding: 1px 4px;
}
.more-info-link {
  color: red;
  text-decoration: underline;
  cursor: pointer;
}
.negative-info-text {
  color: red;
  background-color: yellow;
  padding: 4px 8px;
  font-size: 0.875rem;
}
</style>
